import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    parseISO,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from "date-fns";
import { CalendarPlus, ChevronDown, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";
import { Link } from "react-router-dom";
import BookingWizard from "@/components/BookingWizard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 8..22
const MINUTE_MS = 60000;
const VIEW_OPTIONS = [
    { id: "today", label: "Today" },
    { id: "week", label: "Week" },
    { id: "month", label: "Month" },
    { id: "year", label: "Year" },
];
const VIEW_LABELS = Object.fromEntries(VIEW_OPTIONS.filter((option) => option.id !== "today").map((option) => [option.id, option.label]));
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CalendarPage() {
    const [view, setView] = useState("week"); // day, week, month, year
    const [anchor, setAnchor] = useState(() => new Date());
    const [roomFilter, setRoomFilter] = useState("all");
    const [engineerFilter, setEngineerFilter] = useState("all");
    const [wizardOpen, setWizardOpen] = useState(false);
    const [rescheduleId, setRescheduleId] = useState(null);
    const qc = useQueryClient();

    const range = useMemo(() => {
        if (view === "day") return { start: anchor, end: addDays(anchor, 1) };
        if (view === "month") return { start: startOfMonth(anchor), end: addDays(endOfMonth(anchor), 1) };
        if (view === "year") return { start: startOfYear(anchor), end: addYears(startOfYear(anchor), 1) };
        return {
            start: startOfWeek(anchor, { weekStartsOn: 1 }),
            end: addDays(endOfWeek(anchor, { weekStartsOn: 1 }), 1),
        };
    }, [anchor, view]);
    const { start: startDate, end: endDate } = range;

    const { data: rooms = [] } = useQuery({ queryKey: ["rooms"], queryFn: () => apiGet("/rooms") });
    const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: () => apiGet("/staff") });
    const { data: clients = [] } = useQuery({ queryKey: ["clients-brief"], queryFn: () => apiGet("/clients") });
    const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => apiGet("/services") });
    const { data: bookings = [], refetch } = useQuery({
        queryKey: ["bookings-cal", startDate.toISOString(), endDate.toISOString(), roomFilter, engineerFilter],
        queryFn: () =>
            apiGet("/bookings", {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                room_id: roomFilter !== "all" ? roomFilter : undefined,
                engineer_id: engineerFilter !== "all" ? engineerFilter : undefined,
            }),
    });

    const clientById = useMemo(() => Object.fromEntries((clients || []).map((c) => [c.id, c])), [clients]);
    const roomById = useMemo(() => Object.fromEntries((rooms || []).map((r) => [r.id, r])), [rooms]);
    const staffById = useMemo(() => Object.fromEntries((staff || []).map((s) => [s.id, s])), [staff]);
    const svcById = useMemo(() => Object.fromEntries((services || []).map((s) => [s.id, s])), [services]);

    const calendarDays = useMemo(() => {
        if (view === "day") return [anchor];
        if (view === "week") return Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
        if (view === "month") {
            return eachDayOfInterval({
                start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }),
                end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }),
            });
        }
        return [];
    }, [anchor, startDate, view]);

    const yearMonths = useMemo(
        () => Array.from({ length: 12 }, (_, index) => addMonths(startOfYear(anchor), index)),
        [anchor],
    );

    const move = (dir) => {
        if (view === "day") setAnchor(addDays(anchor, dir));
        else if (view === "week") setAnchor(addWeeks(anchor, dir));
        else if (view === "month") setAnchor(addMonths(anchor, dir));
        else setAnchor(addYears(anchor, dir));
    };

    const selectView = (nextView) => {
        if (nextView === "today") {
            setAnchor(new Date());
            setView("day");
            return;
        }
        setView(nextView);
    };

    const viewMenuLabel = view === "day" && isSameDay(anchor, new Date()) ? "Today" : VIEW_LABELS[view];
    const rangeLabel = view === "day"
        ? format(anchor, "EEE, MMM d, yyyy")
        : view === "week"
            ? `${format(startDate, "MMM d")} – ${format(addDays(endDate, -1), "MMM d, yyyy")}`
            : view === "month"
                ? format(anchor, "MMMM yyyy")
                : format(anchor, "yyyy");

    const bookingsByDay = useMemo(() => {
        const map = new Map();
        for (const bk of bookings) {
            const day = format(parseISO(bk.start_utc), "yyyy-MM-dd");
            if (!map.has(day)) map.set(day, []);
            map.get(day).push(bk);
        }
        return map;
    }, [bookings]);

    const handleReschedule = async (booking, newDate, hour) => {
        const startISO = new Date(newDate);
        startISO.setHours(hour, 0, 0, 0);
        const dur = (parseISO(booking.end_utc) - parseISO(booking.start_utc)) / MINUTE_MS;
        const endISO = new Date(startISO.getTime() + dur * MINUTE_MS);
        try {
            const res = await apiPost(`/bookings/${booking.id}/reschedule`, { start_utc: startISO.toISOString(), end_utc: endISO.toISOString() });
            if (res?.code) {
                toast.error(res.detail || "Reschedule conflict", { description: res.suggestions?.length ? "Alternatives available in the booking detail." : undefined });
            } else {
                toast.success("Booking rescheduled");
                refetch();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reschedule failed");
        }
    };

    const bookingBlock = (bk, compact = false) => {
        const room = roomById[bk.room_id];
        const client = clientById[bk.client_id];
        const isBusy = bk.visibility === "busy" || bk.redacted;
        const durMin = (parseISO(bk.end_utc) - parseISO(bk.start_utc)) / MINUTE_MS;
        return (
            <Link
                key={bk.id}
                to={`/bookings/${bk.id}`}
                draggable={!compact && !isBusy}
                onDragStart={(e) => {
                    if (isBusy) return;
                    e.dataTransfer.setData("text/plain", bk.id);
                    setRescheduleId(bk.id);
                }}
                onDragEnd={() => setRescheduleId(null)}
                style={compact ? { borderLeft: `3px solid ${room?.color || "#0F766E"}` } : { borderLeft: `3px solid ${room?.color || "#0F766E"}`, minHeight: `${Math.max(48, (durMin / 60) * 48)}px` }}
                className={`musook-booking-block block rounded-md p-1.5 mb-1 ${compact ? "hover:bg-muted/60" : isBusy ? "cursor-default" : "cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"}`}
                data-testid={`calendar-booking-${bk.id}`}
            >
                <div className="text-[11px] font-medium leading-tight truncate">{isBusy ? "Studio occupied" : (client?.name || "Client")}</div>
                {!isBusy && <div className="text-[10px] text-muted-foreground truncate">{svcById[bk.service_id]?.name}</div>}
                <div className="text-[10px] text-muted-foreground truncate">{room?.name || "Studio"}</div>
                {!isBusy && <div className="text-[10px] mt-0.5"><StatusPill status={bk.status} /></div>}
            </Link>
        );
    };

    const renderHourlyGrid = () => (
        <div className="overflow-x-auto pretty-scroll">
            <div className="grid musook-calendar-grid" style={{ gridTemplateColumns: `60px repeat(${calendarDays.length}, minmax(180px, 1fr))` }}>
                <div className="bg-muted/50 border-b border-border"></div>
                {calendarDays.map((d) => (
                    <div key={d.toISOString()} className={`bg-muted/50 border-b border-border px-3 py-2 text-sm font-medium ${isSameDay(d, new Date()) ? "text-primary" : ""}`}>
                        <div>{format(d, "EEE")}</div>
                        <div className="font-display text-lg tabular-nums">{format(d, "d")}</div>
                    </div>
                ))}
                {HOURS.flatMap((h) => [
                    <div key={`h-${h}`} className="text-xs text-muted-foreground py-1 pr-2 text-right border-r border-border tabular-nums">
                        {format(new Date().setHours(h, 0, 0, 0), "h a")}
                    </div>,
                    ...calendarDays.map((d) => {
                        const dayKey = format(d, "yyyy-MM-dd");
                        const dayBookings = (bookingsByDay.get(dayKey) || []).filter((b) => parseISO(b.start_utc).getHours() === h);
                        return (
                            <div
                                key={`c-${h}-${d.toISOString()}`}
                                className="min-h-[48px] border-b border-r border-border p-1 relative"
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    const id = e.dataTransfer.getData("text/plain");
                                    const bk = bookings.find((b) => b.id === id);
                                    if (bk) handleReschedule(bk, d, h);
                                }}
                                data-testid={`calendar-cell-${dayKey}-${h}`}
                            >
                                {dayBookings.map((bk) => bookingBlock(bk))}
                            </div>
                        );
                    }),
                ])}
            </div>
        </div>
    );

    const renderMonthGrid = () => (
        <div className="overflow-x-auto pretty-scroll">
            <div className="min-w-[980px] grid grid-cols-7 border-l border-t border-border">
                {WEEKDAY_LABELS.map((label) => <div key={label} className="bg-muted/50 border-r border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>)}
                {calendarDays.map((day) => {
                    const dayKey = format(day, "yyyy-MM-dd");
                    const dayBookings = bookingsByDay.get(dayKey) || [];
                    const inCurrentMonth = day.getMonth() === anchor.getMonth();
                    return (
                        <div key={dayKey} className={`min-h-[142px] border-r border-b border-border p-2 ${inCurrentMonth ? "bg-card" : "bg-muted/20 text-muted-foreground"}`} data-testid={`calendar-month-day-${dayKey}`}>
                            <div className={`mb-2 text-sm font-semibold ${isSameDay(day, new Date()) ? "text-primary" : ""}`}>{format(day, "d")}</div>
                            {dayBookings.slice(0, 4).map((bk) => bookingBlock(bk, true))}
                            {dayBookings.length > 4 && <div className="px-1 text-[10px] text-muted-foreground">+{dayBookings.length - 4} more</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderYearGrid = () => (
        <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {yearMonths.map((month) => {
                const monthStart = startOfMonth(month);
                const monthEnd = addMonths(monthStart, 1);
                const monthBookings = bookings.filter((bk) => {
                    const date = parseISO(bk.start_utc);
                    return date >= monthStart && date < monthEnd;
                });
                return (
                    <div key={month.toISOString()} className="rounded-lg border border-border bg-card p-4" data-testid={`calendar-year-month-${format(month, "yyyy-MM")}`}>
                        <div className="flex items-center justify-between"><h3 className="font-display font-semibold">{format(month, "MMMM")}</h3><span className="text-xs text-muted-foreground">{monthBookings.length} booking{monthBookings.length === 1 ? "" : "s"}</span></div>
                        <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">{WEEKDAY_LABELS.map((label) => <span key={label}>{label.slice(0, 1)}</span>)}</div>
                        <div className="mt-1 grid grid-cols-7 gap-1 text-center text-xs">{eachDayOfInterval({ start: startOfWeek(monthStart, { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) }).map((day) => { const count = (bookingsByDay.get(format(day, "yyyy-MM-dd")) || []).length; return <span key={day.toISOString()} className={`rounded py-1 ${day.getMonth() === month.getMonth() ? "" : "text-muted-foreground/40"} ${count ? "bg-primary/15 font-semibold text-primary" : ""}`} title={count ? `${count} booking${count === 1 ? "" : "s"}` : undefined}>{format(day, "d")}</span>; })}</div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="musook-page w-full px-4 py-6 sm:px-6 lg:px-8" data-testid="calendar-page">
            <PageHeader
                title="Calendar"
                subtitle="A TV-guide view of every room, engineer, and session. Drag to reschedule; conflicts are enforced server-side."
                testId="calendar-header"
                actions={
                    <>
                        <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1" role="tablist" aria-label="Calendar view tabs">
                            {["day", "week"].map((tab) => (
                                <button
                                    key={tab}
                                    role="tab"
                                    aria-selected={view === tab}
                                    data-testid={`calendar-tab-${tab}`}
                                    onClick={() => setView(tab)}
                                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${view === tab ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-background hover:text-foreground"}`}
                                >
                                    {tab === "day" ? "Day" : "Week"}
                                </button>
                            ))}
                        </div>
                        <Select value={roomFilter} onValueChange={setRoomFilter}>
                            <SelectTrigger className="w-[180px]" data-testid="calendar-room-filter">
                                <Filter className="h-3.5 w-3.5 mr-1" />
                                <SelectValue placeholder="All rooms" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All rooms</SelectItem>
                                {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={engineerFilter} onValueChange={setEngineerFilter}>
                            <SelectTrigger className="w-[180px]" data-testid="calendar-engineer-filter">
                                <SelectValue placeholder="All engineers" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All engineers</SelectItem>
                                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button onClick={() => setWizardOpen(true)} data-testid="calendar-new-booking">
                            <CalendarPlus className="mr-2 h-4 w-4" /> New booking
                        </Button>
                    </>
                }
            />

            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => move(-1)} data-testid="calendar-prev" aria-label={`Previous ${view}`}><ChevronLeft className="h-4 w-4" /></Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" data-testid="calendar-view-menu" aria-label="Choose calendar range">
                                {viewMenuLabel}<ChevronDown className="ml-1 h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            {VIEW_OPTIONS.map((option) => <DropdownMenuItem key={option.id} onSelect={() => selectView(option.id)} data-testid={`calendar-view-option-${option.id}`}>{option.label}</DropdownMenuItem>)}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="ghost" size="icon" onClick={() => move(1)} data-testid="calendar-next" aria-label={`Next ${view}`}><ChevronRight className="h-4 w-4" /></Button>
                </div>
                <div className="flex items-center gap-3 font-display text-lg font-semibold"><span className="musook-status-dot" />{rangeLabel}</div>
            </div>

            <Card className="musook-surface border-border overflow-hidden rounded-xl" data-testid={`calendar-view-${view}`}>
                {view === "day" || view === "week" ? renderHourlyGrid() : view === "month" ? renderMonthGrid() : renderYearGrid()}
            </Card>

            <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
                <DialogContent className="max-w-3xl" aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle className="font-display">New booking</DialogTitle>
                    </DialogHeader>
                    <BookingWizard
                        onSuccess={() => { setWizardOpen(false); refetch(); qc.invalidateQueries({ queryKey: ["dashboard-today"] }); }}
                        onCancel={() => setWizardOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
